"use client";

import { useState, useRef, useEffect } from "react";
import { Send, Check, Loader2 } from "lucide-react";
import Link from "next/link";
import { useLocale } from "@/lib/useLocale";
import { t } from "@/lib/translations";

interface ProductInquiryFormProps {
  productId: string;
  productTitle: string;
}

export default function ProductInquiryForm({
  productId,
  productTitle,
}: ProductInquiryFormProps) {
  const { locale } = useLocale();
  const formRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [quantity, setQuantity] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-100px 0px 0px 0px" }
    );

    if (formRef.current) {
      observer.observe(formRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch("/api/export-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          company: "",
          country: "",
          products: productTitle,
          quantity: quantity || "Not specified",
          message: message || `Inquiry regarding ${productTitle}`,
        }),
      });

      if (res.ok) {
        setSubmitted(true);
      }
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-brand-50 rounded-xl p-6 text-center border border-brand-200">
        <div className="w-12 h-12 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-3">
          <Check className="w-6 h-6 text-brand-600" />
        </div>
        <h3 className="font-semibold text-brand-900 mb-1">{t(locale, "inquiry.sent")}</h3>
        <p className="text-sm text-brand-700">
          {t(locale, "inquiry.respond")}
        </p>
      </div>
    );
  }

  const formContent = (
    <form onSubmit={handleSubmit} className="space-y-3">
      <input
        type="text"
        placeholder={t(locale, "inquiry.namePlaceholder")}
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full px-3.5 py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-shadow"
      />
      <input
        type="email"
        placeholder={t(locale, "inquiry.emailPlaceholder")}
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full px-3.5 py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-shadow"
      />
      <input
        type="text"
        placeholder={t(locale, "inquiry.qtyPlaceholder")}
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        className="w-full px-3.5 py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-shadow"
      />
      <textarea
        placeholder={t(locale, "inquiry.messagePlaceholder")}
        rows={3}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        className="w-full px-3.5 py-2.5 text-base sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition-shadow resize-none"
      />

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-brand-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-brand-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {t(locale, "inquiry.send")}
      </button>

      <p className="text-xs text-gray-400 text-center">
        {t(locale, "inquiry.respondWithin")}
      </p>
    </form>
  );

  return (
    <>
      <div ref={formRef}>
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-1">{t(locale, "inquiry.title")}</h3>
          <p className="text-sm text-gray-500 mb-4">
            {t(locale, "inquiry.desc")}
          </p>
          {formContent}
        </div>
      </div>

      {isSticky && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slideUp" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.08)] border-t border-gray-200 px-4 py-3">
            <div className="max-w-6xl mx-auto">
              {/* Mobile layout */}
              <div className="md:hidden flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {productTitle}
                  </p>
                  <p className="text-xs text-gray-500">{t(locale, "inquiry.mobileLabel")}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const form = formRef.current;
                    if (form) {
                      form.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className="bg-brand-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors flex items-center gap-2 shrink-0 min-h-[44px]"
                >
                  <Send className="w-4 h-4" />
                  {t(locale, "nav.inquiry")}
                </button>
              </div>

              {/* Desktop layout */}
              <div className="hidden md:flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {productTitle}
                  </p>
                  <p className="text-xs text-gray-500">{t(locale, "inquiry.desktopLabel")}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    type="email"
                    placeholder={t(locale, "inquiry.emailPlaceholder").replace(" *", "")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-40 lg:w-56 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  />
                  <input
                    type="text"
                    placeholder={t(locale, "inquiry.qtyPlaceholder")}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const form = formRef.current;
                      if (form) {
                        form.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-3.5 h-3.5" />
                    {t(locale, "nav.inquiry")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
